import * as React from 'react'
import { useCallback } from 'react'

import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import setLanguageCookie from 'cookies/setLanguageCookie'
import UrlLocation from 'src/lib/url/UrlLocation'
import { useLocaleUrlOptions } from 'hooks/useLocaleUrlOptions'
import { reportClientError } from 'services/errorReportingService'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { syncUserDetails } from 'actions/AuthActions'
import { updateCustomerLanguagePreferenceFromAuth } from 'api/auth'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'

function NavBarLanguageItems(props: {
  onLanguageSelect?: (e: React.MouseEvent<HTMLButtonElement>, changed: boolean) => void
}) {
  const { onLanguageSelect } = props
  const dispatch = useAppDispatch()
  const { locale, languageOptions } = useLocaleUrlOptions()
  const isLoggedIn = useAppSelector(selectLoggedIn)

  const currentLanguage =
    languageOptions.find((lang) => lang.locale === locale) ??
    languageOptions[0]

  const onLanguageClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      const selectedLanguage = languageOptions.find(
        (language) => language.locale === e.currentTarget.dataset.locale,
      )

      if (selectedLanguage) {
        const changed = selectedLanguage.locale !== currentLanguage?.locale
        if (changed) {
          setLanguageCookie(selectedLanguage.locale)
          if (isLoggedIn) {
            updateCustomerLanguagePreferenceFromAuth(selectedLanguage.locale)
              .catch(reportClientError)
            dispatch(syncUserDetails({ language: selectedLanguage.locale }))
          }
          const currentLocationUrl = UrlLocation.fromLocation(window.location)
          const redirect = currentLocationUrl.getRedirectToLocale({
            targetLocale: selectedLanguage.locale,
            languageOptions,
          })
          const targetUrl = currentLocationUrl.withRedirect(redirect)

          if (!targetUrl.equals(currentLocationUrl)) {
            window.location.href = targetUrl.toString()
          }
        }
        onLanguageSelect?.(e, changed)
      }
    },
    [dispatch, currentLanguage?.locale, languageOptions, onLanguageSelect, isLoggedIn],
  )

  return (
    <>
      {languageOptions.map((language) => (
        <ClickableListItem
          size="medium"
          key={language.locale}
          endIcon={
            currentLanguage?.locale === language.locale ? (
              <LineCheckIcon />
            ) : undefined
          }
          title={language.nativeName}
          data-locale={language.locale}
          onClick={onLanguageClick}
          selected={currentLanguage?.locale === language.locale}
        />
      ))}
    </>
  )
}

export default React.memo(NavBarLanguageItems)
